// Firma del contrato (FES, crédito SureKeep): resumen del crédito, IMEI del equipo y el contrato,
// que se abre, acepta y firma con un código en `ContractSignModal`.
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Check, FileText, Signature } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { useContractPreview, useWizardSession } from '../api/useWizard';
import { ContractDeviceBlock } from '../components/ContractDeviceBlock';
import { ContractSignModal } from '../components/ContractSignModal';
import { financedImeiRequired, pinnedFinancedImei } from '../deliveryPieces';
import { FINANCED_IMEI_NOT_PINNED_MESSAGE } from '../financedImeiErrors';
import { wizardErrorMsg } from '../api/wizardError';
import {
    INTEGRATION_ACTION_LABEL,
    INTEGRATION_STEP,
    type IntegrationFailure,
} from '../integrationErrors';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { creditTermsSummaryOf, type CreditTermsSummary } from '../creditTermsSummary';
import { OTP_RESEND_SECONDS } from '../otpResend';

const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

const mxn = (cents: number): string => formatMXN(cents / 100);

/** Lo que paga cada semana y el total del crédito; plazo, financiado y tasa en una línea chica. */
function CreditTermsCard({ summary }: { summary: CreditTermsSummary }) {
    return (
        <section
            data-testid="sign-credit-summary"
            aria-label="Resumen del crédito"
            className="flex flex-col divide-y divide-border rounded-md border border-border bg-card"
        >
            <div className="flex items-baseline justify-between gap-3 p-4">
                <span className="text-[13px] font-semibold uppercase tracking-[0.06em] text-promo">
                    Cada semana
                </span>
                <span
                    data-testid="sign-credit-payments"
                    className="text-[22px] font-bold tabular-nums text-promo"
                >
                    {mxn(summary.weeklyCents)}
                </span>
            </div>
            <div className="flex flex-col gap-1 p-4">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                        Total del crédito
                    </span>
                    <span
                        data-testid="sign-credit-total"
                        className="text-[22px] font-bold tabular-nums"
                    >
                        {mxn(summary.totalCents)}
                    </span>
                </div>
                <p
                    data-testid="sign-credit-terms"
                    className="text-right text-[11.5px] text-muted-foreground tabular-nums"
                >
                    {summary.weeks} semanas · financiado {mxn(summary.financedCents)} · tasa{' '}
                    {summary.tnaPct}% anual
                </p>
            </div>
        </section>
    );
}

export function SignStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);

    const [editingImei, setEditingImei] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [signedLocally, setSignedLocally] = useState(false);
    // Viven aquí y no en el modal para que cerrarlo no pierda el código enviado ni su espera.
    const [accepted, setAccepted] = useState(false);
    const [documentOpened, setDocumentOpened] = useState(false);
    /** Destino enmascarado (`****1234`) — presente = el código ya se despachó. */
    const [masked, setMasked] = useState<string | null>(null);
    const [resendIn, setResendIn] = useState(0);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = setTimeout(() => {
            setResendIn((n) => n - 1);
        }, 1000);
        return () => {
            clearTimeout(timer);
        };
    }, [resendIn]);

    const creditId = session?.loan?.creditId;
    const preview = useContractPreview(sessionId, !!session && !!creditId);
    const credit = preview.data?.credit;
    // Partial: el wire solo manda las llaves que existen (curp/domicilio son opcionales).
    const contractData: Partial<Record<string, string>> = preview.data?.contractData ?? {};

    if (!session) return <Loading size="lg" />;

    // `currentStep` guarda el último paso COMPLETADO: tras firmar queda '08'.
    const signed = signedLocally || parseInt(session.currentStep, 10) >= 8;
    const imeiRequired = financedImeiRequired(session);
    // Sin IMEI guardado no hay contrato que leer ni firmar; lo ya firmado se queda como está.
    const imeiReady =
        !imeiRequired || signed || (pinnedFinancedImei(session) !== '' && !editingImei);

    if (!creditId) {
        return (
            <WizardCard>
                <WizardCardHead eyebrow="Crédito SureKeep" title="Firma del contrato" />
                <WizardCardBody>
                    <p className="text-[12.5px] text-muted-foreground">
                        Todavía no hay un crédito cotizado. Regresa a Cotización y confírmala.
                    </p>
                </WizardCardBody>
                <WizardFootNav
                    back={
                        <WizardBackLink
                            sessionId={sessionId}
                            step="quote"
                            label="← Volver a la cotización"
                        />
                    }
                />
            </WizardCard>
        );
    }

    /** Rechazo de Zentral: se relee la venta y el aviso lleva al paso que lo resuelve. */
    const reportIntegration = (failure: IntegrationFailure): void => {
        void refresh();
        if (failure.recovery === 'none') {
            toast.error(failure.message);
            return;
        }
        const step = INTEGRATION_STEP[failure.recovery];
        toast.error(failure.message, {
            action: {
                label: INTEGRATION_ACTION_LABEL[failure.recovery],
                onClick: () => {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step },
                    });
                },
            },
        });
    };

    return (
        <WizardCard>
            <WizardCardHead eyebrow="Crédito SureKeep" title="Firma del contrato" />

            <WizardCardBody className="flex flex-col gap-4">
                {preview.isPending ? (
                    <Loading size="lg" />
                ) : preview.isError ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {wizardErrorMsg(
                                preview.error,
                                'No se pudo leer el resumen del contrato.',
                            )}
                        </p>
                        <Button
                            size="sm"
                            isLoading={preview.isFetching}
                            onClick={() => void preview.refetch()}
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : (
                    <>
                        {credit && <CreditTermsCard summary={creditTermsSummaryOf(credit)} />}

                        {imeiRequired && (
                            <div className="rounded-[10px] border border-border bg-card p-4">
                                <ContractDeviceBlock
                                    sessionId={sessionId}
                                    session={session}
                                    contractData={contractData}
                                    locked={signed || masked !== null}
                                    editing={editingImei}
                                    onEditingChange={(editing) => {
                                        setEditingImei(editing);
                                        // Otro IMEI cambia el contrato: se vuelve a leer y aceptar.
                                        if (editing) {
                                            setAccepted(false);
                                            setDocumentOpened(false);
                                        }
                                    }}
                                    onPinned={() => void preview.refetch()}
                                    onReload={() => void refresh()}
                                />
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-3 rounded-[10px] border border-border bg-card p-4">
                            <div className="flex min-w-0 items-center gap-3">
                                <span className="flex size-10 flex-none items-center justify-center rounded-[10px] bg-info-soft text-info">
                                    <FileText className="size-5" aria-hidden />
                                </span>
                                <div className="min-w-0">
                                    <div className="text-[13px] font-semibold">
                                        Contrato de crédito
                                    </div>
                                    {signed ? (
                                        <div className="mt-0.5 flex items-center gap-1 text-[11.5px] font-semibold text-success">
                                            <Check
                                                className="size-3.5"
                                                strokeWidth={3}
                                                aria-hidden
                                            />
                                            Firmado
                                        </div>
                                    ) : (
                                        <div className={cn(HINT, 'mt-0.5')}>
                                            {imeiReady
                                                ? 'El cliente lo lee, acepta y firma con un código.'
                                                : 'Primero guarda el IMEI del equipo.'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Button
                                size="sm"
                                variant={signed ? 'outline' : 'default'}
                                disabled={!imeiReady}
                                rightIcon={<Signature className="size-4" aria-hidden />}
                                onClick={() => {
                                    setModalOpen(true);
                                }}
                            >
                                {signed ? 'Ver contrato' : 'Ver y firmar'}
                            </Button>
                        </div>
                    </>
                )}
            </WizardCardBody>

            <WizardFootNav back={<WizardBackLink sessionId={sessionId} step="payment" />}>
                <Button
                    disabled={!signed}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({
                            to: '/wizard/s/$sessionId/$step',
                            params: { sessionId, step: 'delivery' },
                        })
                    }
                >
                    Continuar a Entrega
                </Button>
            </WizardFootNav>

            <ContractSignModal
                sessionId={sessionId}
                customerName={contractData.nombreAcreditado}
                phone={session.phone}
                signed={signed}
                open={modalOpen && imeiReady}
                onClose={() => {
                    setModalOpen(false);
                }}
                onSigned={() => {
                    setSignedLocally(true);
                }}
                accepted={accepted}
                onAcceptedChange={setAccepted}
                documentOpened={documentOpened}
                onDocumentOpened={() => {
                    setDocumentOpened(true);
                }}
                masked={masked}
                onCodeSent={(destination) => {
                    setMasked(destination);
                    setResendIn(OTP_RESEND_SECONDS);
                }}
                resendIn={resendIn}
                onResendWait={setResendIn}
                onIntegrationFailure={reportIntegration}
                onImeiNotPinned={() => {
                    toast.error(FINANCED_IMEI_NOT_PINNED_MESSAGE);
                    void refresh();
                }}
            />
        </WizardCard>
    );
}
